import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { TdLoadingService } from '@covalent/core/loading';
import { format } from 'date-fns';
import { TypedFormBuilder, TypedFormGroup } from 'formsafe';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  share,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { EmployeeHttpClient } from 'src/app/shared/flight-log-api/employees/employee-client.service';
import { EntityType } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { PilotHttpClient } from 'src/app/shared/flight-log-api/pilots/pilot-client.service';
import {
  UserActivityReportHttpClient,
  UserActivityReportQuery,
} from 'src/app/shared/flight-log-api/user-activity-reports/user-activity-reports-client.service';
import { humanizeEntityName } from 'src/app/transmission-runs/components/transmission-run-detail-list-page/transmission-run-detail-list-page.component';
import { EmployeeEntity } from 'src/store/employee/employee.model';
import { UserActivityReportEntity } from 'src/store/user-activity-report/user-activity-report.model';
import { SelectItem, toSelectionList } from 'src/util/ui/forms';
import {
  getPageIndex,
  getPageQueryParams,
  getPageSize,
  tryGetSkipTakeOrDefault,
} from 'src/util/ui/pagination';
import { parseIntOrDefault } from 'src/util/values';
import { AirCarrierViewModel } from 'src/view-models/air-carrier-view-model';
import { PilotViewModel } from 'src/view-models/pilot-view-model';

type UserActivityReportQueryForm = Omit<UserActivityReportQuery, 'skip' | 'take' | 'carrierId'>;
interface RouteParams extends UserActivityReportQueryForm {
  page?: string;
  pageSize?: string;
}

@Component({
  selector: 'fl-user-activity-report-list-page',
  templateUrl: './user-activity-report-list-page.component.html',
  styleUrls: ['./user-activity-report-list-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserActivityReportListPageComponent implements OnInit, OnDestroy {
  public userActivityReports$: Observable<UserActivityReportEntity[]>;
  public total$: Observable<number>;
  public defaultPageSize = 10;
  public pageSizeOptions = [this.defaultPageSize, 20, 40, 80];
  public page$ = new BehaviorSubject<PageEvent>({
    pageIndex: 0,
    pageSize: this.defaultPageSize,
    length: 0,
  });
  public pageIndex$: Observable<number>;
  public pageSize$: Observable<number | undefined>;
  public isValidDateRange$: Observable<boolean>;
  public loadingKey = 'user-activity-reports-table';
  public userActivityReportParameters: TypedFormGroup<UserActivityReportQueryForm>;
  public airCarrier$: Observable<AirCarrierViewModel>;
  public _pageRefresh$ = new BehaviorSubject<{}>({});
  public _onDestroy$ = new Subject();

  public pilotOptions$: Observable<SelectItem[]>;
  public employeeOptions$: Observable<SelectItem[]>;
  public entityTypeOptions = Object.values(EntityType);

  public pilotSearch$ = new Subject<string>();
  public employeeSearch$ = new Subject<string>();

  public selectedPilot: SelectItem;
  public selectedEmployee: SelectItem;

  public defaultStartDate = new Date(new Date().setDate(new Date().getDate() - 30));
  public defaultEndDate = new Date(Date.now());

  public startDateCtrl = new FormControl(this.defaultStartDate);
  public endDateCtrl = new FormControl(this.defaultEndDate);
  public maxDate = new Date(Date.now());

  public displayedColumns = ['pilotName', 'entityType', 'createdDate', 'lastUpdate'];

  constructor(
    private _userActivityReport: UserActivityReportHttpClient,
    private _route: ActivatedRoute,
    private _router: Router,
    private _loading: TdLoadingService,
    private _tfb: TypedFormBuilder,
    private _pilot: PilotHttpClient,
    private _employee: EmployeeHttpClient,
  ) {
    this.airCarrier$ = this._route.data.pipe(
      map((data) => data.airCarrier),
      distinctUntilChanged(),
    );
    const userActivityReportsResults$ = combineLatest([
      this._route.data.pipe(map((data) => data.airCarrier)),
      _route.queryParams,
      this._pageRefresh$,
    ]).pipe(
      switchMap(([airCarrier, queryParams]) => {
        const params = this.parseRouteParams(queryParams);
        this.markAsLoading();
        return this._userActivityReport.getByAirCarrierId({
          carrierId: airCarrier?.id,
          // skip: params.skip,
          // take: params.take,
          startTime:
            params.startTime !== undefined
              ? format(params.startTime, 'MM-DD-YYYY')
              : format(this.defaultStartDate.toDateString(), 'MM-DD-YYYY'),
          endTime:
            params.endTime !== undefined
              ? format(params.endTime, 'MM-DD-YYYY')
              : format(this.defaultEndDate.toDateString(), 'MM-DD-YYYY'),
          entityType: params.entityType,
          lastUpdatedById: params.lastUpdatedById,
          pilotId: params.pilotId,
          skip: params.skip,
          take: params.take,
        });
      }),
      distinctUntilChanged(),
      tap(() => this.markAsLoaded()),
      share(),
    );
    this.userActivityReports$ = userActivityReportsResults$.pipe(map((r) => r.results));

    this.total$ = userActivityReportsResults$.pipe(map((r) => r.totalCount));
  }

  public ngOnInit() {
    this.userActivityReportParameters = this._tfb.group<UserActivityReportQueryForm>({
      startTime: [undefined],
      endTime: [undefined],
      entityType: [undefined],
      lastUpdatedById: [undefined],
      pilotId: [undefined],
    });

    this.userActivityReportParameters.valueChanges
      .pipe(debounceTime(500), takeUntil(this._onDestroy$))
      .subscribe((value) => {
        this.patchQueryParams(this.normalizeParams(value));
      });

    this._route.queryParams.pipe(takeUntil(this._onDestroy$)).subscribe((params: RouteParams) => {
      if (params.startTime) {
        this.startDateCtrl.setValue(new Date(params.startTime));
      } else {
        this.startDateCtrl.setValue(this.defaultStartDate);
      }
      if (params.endTime) {
        this.endDateCtrl.setValue(new Date(params.endTime));
      } else {
        this.endDateCtrl.setValue(this.defaultEndDate);
      }
      this.userActivityReportParameters.patchValue(this.normalizeParams(params), {
        emitEvent: false,
      });
      this.patchQueryParams(params);
    });

    if (this.userActivityReportParameters.controls.lastUpdatedById.value) {
      this._employee
        .getById(this.userActivityReportParameters.controls.lastUpdatedById.value)
        .pipe(take(1))
        .subscribe((employee) => {
          const employeeName = employee?.firstName + ' ' + employee?.lastName;
          this.selectedEmployee = { value: employee?.id, label: employeeName };
        });
    }

    if (this.userActivityReportParameters.controls.pilotId.value) {
      this._pilot
        .getById(this.userActivityReportParameters.controls.pilotId.value)
        .pipe(take(1))
        .subscribe((r) => {
          const pilot = r.results[0];
          const pilotName = pilot.firstName + ' ' + pilot.lastName;
          this.selectedPilot = { value: pilot.id, label: pilotName };
        });
    }

    this.pageIndex$ = getPageIndex(this._route);
    this.pageSize$ = getPageSize(this._route, this.defaultPageSize);
    this.page$.pipe(takeUntil(this._onDestroy$)).subscribe((page) => {
      this.patchQueryParams(getPageQueryParams(page, this.defaultPageSize));
    });

    this.pilotOptions$ = this.airCarrier$.pipe(
      switchMap((airCarrier) => this.observePilotOptions(this.pilotSearch$, airCarrier.id)),
    );
    this.employeeOptions$ = this.observeEmployeeOptions(this.employeeSearch$);
    this.startDateCtrl.valueChanges
      .pipe(debounceTime(500), takeUntil(this._onDestroy$))
      .subscribe((value) => {
        this.userActivityReportParameters.controls.startTime.setValue(value.toDateString());
      });

    this.endDateCtrl.valueChanges
      .pipe(debounceTime(500), takeUntil(this._onDestroy$))
      .subscribe((value) => {
        this.userActivityReportParameters.controls.endTime.setValue(value.toDateString());
      });
  }

  public ngOnDestroy() {}

  public onPage(page: PageEvent) {
    this.page$.next(page);
  }

  public markAsLoading() {
    this._loading.register(this.loadingKey);
  }

  public markAsLoaded() {
    this._loading.resolveAll(this.loadingKey);
  }

  public patchQueryParams<T extends {}>(params: T | undefined) {
    params = params != null ? params : ({} as any);
    const airCarrierAbbreviation = this._route.snapshot.data.airCarrier.abbreviation;
    const currentQueryparams = this._route.snapshot.queryParams;
    const queryParams = {
      ...currentQueryparams,
      ...params,
    };
    this._router.navigate([`/prd/${airCarrierAbbreviation}/user-activity-reports`], {
      queryParams,
    });
  }

  public refresh() {
    this._pageRefresh$.next({});
  }

  public onPilotSearch(searchTerm: string) {
    return this.pilotSearch$.next(searchTerm);
  }

  public onEmployeeSearch(searchTerm: string) {
    return this.employeeSearch$.next(searchTerm);
  }

  public observePilotOptions(searchTerm: Observable<string | null>, airCarrierId: string) {
    return searchTerm
      .pipe(startWith(null as string | null))
      .pipe(
        switchMap((search) =>
          this._pilot.getByAirCarrierId({
            searchTerm: search ?? undefined,
            take: 25,
            airCarrierId,
          }),
        ),
      )
      .pipe(map((r) => r.results?.sort((a, b) => a.lastName.localeCompare(b.lastName))))
      .pipe(
        map(
          toSelectionList<PilotViewModel>((pilot) => `${pilot.firstName} ${pilot.lastName}`, 'id'),
        ),
      );
  }

  public observeEmployeeOptions(searchTerm: Observable<string | null>) {
    return searchTerm
      .pipe(startWith(null as string | null))
      .pipe(
        switchMap((search) =>
          this._employee.getAll({
            searchTerm: search ?? undefined,
            $top: 25,
          }),
        ),
      )
      .pipe(map((e) => e.sort((a, b) => a.lastName.localeCompare(b.lastName))))
      .pipe(
        map(
          toSelectionList<EmployeeEntity>(
            (employee) => `${employee.firstName} ${employee.lastName}`,
            'id',
          ),
        ),
      );
  }

  public humanizeEntityName(entityName: string) {
    return humanizeEntityName(entityName);
  }

  public resetFilters() {
    this.userActivityReportParameters.reset();
    this.userActivityReportParameters.controls.startTime.setValue(
      this.defaultStartDate.toDateString(),
    );
    this.userActivityReportParameters.controls.endTime.setValue(this.defaultEndDate.toDateString());
  }

  private normalizeParams(params: RouteParams): UserActivityReportQueryForm {
    return {
      entityType: params.entityType,
      lastUpdatedById: params.lastUpdatedById,
      pilotId: params.pilotId,
      startTime:
        params.startTime !== undefined ? format(params.startTime, 'MM-DD-YYYY') : undefined,
      endTime: params.endTime !== undefined ? format(params.endTime, 'MM-DD-YYYY') : undefined,
    };
  }

  private parseRouteParams(queryParams: RouteParams): UserActivityReportQuery {
    const pageSize = parseIntOrDefault(queryParams.pageSize, this.defaultPageSize);
    return {
      ...this.normalizeParams(queryParams),
      ...tryGetSkipTakeOrDefault(queryParams, pageSize),
    };
  }
}
